import React from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { repairService } from '../../services/repair.service';
import { RepairStatusBadge } from '../../components/repairs/RepairStatusBadge';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { Loader2, ArrowLeft, FileText, Image, Smartphone, Wrench, MessageCircle, Truck, Check, CreditCard, Download, ChevronDown, ChevronUp } from 'lucide-react';

const statusSteps = [
  { status: 'SUBMITTED', label: 'Submitted', description: 'Your repair request has been received' },
  { status: 'IN_PROGRESS', label: 'In Progress', description: 'Our technicians are working on your device' },
  { status: 'WAITING_PARTS', label: 'Waiting for Parts', description: 'Waiting for replacement parts to arrive' },
  { status: 'COMPLETED', label: 'Completed', description: 'Repair work is complete' },
  { status: 'DELIVERED', label: 'Delivered', description: 'Your device has been delivered' },
  { status: 'CLOSED', label: 'Closed', description: 'This repair has been closed' },
];

export const RepairDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const repairId = parseInt(id || '0');
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [paymentAmount, setPaymentAmount] = React.useState('0.00');
  const [paymentMethod, setPaymentMethod] = React.useState<'paystack_card' | 'mobile_money' | 'bank_transfer' | 'cash'>('paystack_card');
  const [paymentMessage, setPaymentMessage] = React.useState('');
  const [paymentVerified, setPaymentVerified] = React.useState(false);
  const [isInvoiceCollapsed, setIsInvoiceCollapsed] = React.useState(false);
  const reference = searchParams.get('reference');

  const { data: repair, isLoading: repairLoading } = useQuery({
    queryKey: ['repair', repairId],
    queryFn: () => repairService.getRepair(repairId),
    enabled: !!repairId,
  });

  const defaultPaymentAmount = repair?.invoice ? (repair.invoice.balance / 100).toFixed(2) : '0.00';

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['repair-history', repairId],
    queryFn: () => repairService.getStatusHistory(repairId),
    enabled: !!repairId,
  });
  const approveMutation = useMutation({
    mutationFn: () => repairService.approveEstimate(repairId),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['repair', repairId] }); setPaymentMessage('Estimate approved. You can now make a payment.'); },
  });

  React.useEffect(() => {
    if (repair?.invoice) {
      setPaymentAmount((repair.invoice.balance / 100).toFixed(2));
    }
  }, [repair?.invoice?.balance]);

  const normalizedPaymentAmount = Number.parseFloat(String(paymentAmount || defaultPaymentAmount || '0'));
  const hasValidPayment = Number.isFinite(normalizedPaymentAmount) && normalizedPaymentAmount > 0;
  const paymentRequestKey = React.useMemo(
    () => `${repairId}:${paymentMethod}:${normalizedPaymentAmount.toFixed(2)}`,
    [repairId, paymentMethod, normalizedPaymentAmount],
  );

  const verifyPaymentMutation = useMutation({
    mutationFn: () => repairService.verifyInvoicePayment(repairId, reference!),
    onSuccess: (result) => {
      if (result.status === 'success') {
        setPaymentVerified(true);
        setPaymentMessage('Payment successful! Your repair invoice has been updated.');
      } else if (result.status === 'pending') {
        setPaymentMessage('Payment is still being verified. We will confirm it shortly.');
      } else {
        setPaymentMessage('Payment could not be verified yet. Please try again or contact support.');
      }
      void queryClient.invalidateQueries({ queryKey: ['repair', repairId] });
    },
    onError: () => {
      setPaymentMessage('Payment verification is still pending. Please refresh once the gateway confirms it.');
    },
  });

  React.useEffect(() => {
    if (!reference || !repairId || paymentVerified) return;
    verifyPaymentMutation.mutate();
  }, [reference, repairId, paymentVerified]);

  React.useEffect(() => {
    if (repair?.invoice && repair.invoice.balance <= 0) {
      setPaymentVerified(true);
      setPaymentMessage('Payment successful! Your repair invoice is fully settled.');
    }
  }, [repair?.invoice?.balance]);

  const payMutation = useMutation({
    mutationFn: () => repairService.payInvoice(repairId, Math.round(normalizedPaymentAmount * 100), paymentMethod, paymentRequestKey),
    onSuccess: (result) => {
      if (result.paymentUrl) {
        window.location.assign(result.paymentUrl);
        return;
      }
      setPaymentMessage(result.instructions || 'Payment recorded and awaiting confirmation.');
      if (result.status === 'success') {
        setPaymentVerified(true);
      }
      void queryClient.invalidateQueries({ queryKey: ['repair', repairId] });
    },
    onError: () => setPaymentMessage('Payment could not be started. Please try again.'),
  });

  if (repairLoading || historyLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!repair) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-[rgb(var(--light-secondary))]">Repair not found</h2>
        <Link to="/dashboard/repairs" className="mt-4 inline-block text-primary hover:underline">
          Back to repairs
        </Link>
      </div>
    );
  }

  const currentStepIndex = statusSteps.findIndex((s) => s.status === repair.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--foreground))]">
            Repair #{repair.repairNumber}
          </h1>
          <div className="mt-1 flex items-center space-x-3">
            <RepairStatusBadge status={repair.status} size="lg" />
            <span className="text-sm text-[rgb(var(--light-secondary))]">
              Submitted {new Date(repair.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <Link to="/dashboard/repairs">
          <Button variant="secondary" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Repairs
          </Button>
        </Link>
      </div>

      {/* Status Timeline */}
      <Card>
        <CardTitle>Status Timeline</CardTitle>
        <CardContent>
          <div className="relative">
            <div className="absolute left-5 top-0 h-full w-0.5 bg-[rgb(var(--border))]" />
            <div className="space-y-6">
              {statusSteps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                return (
                  <div key={step.status} className="relative flex items-start">
                    <div
                      className={`relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                        isCompleted
                          ? 'border-primary bg-primary text-white'
                          : 'border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--light-secondary))]'
                      }`}
                    >
                      {isCompleted && <Check className="h-5 w-5" />}
                      {!isCompleted && <div className="h-2 w-2 rounded-full bg-[rgb(var(--border))]" />}
                    </div>
                    <div className="ml-4">
                      <h4 className={`font-semibold ${isCompleted ? 'text-[rgb(var(--foreground))]' : 'text-[rgb(var(--light-secondary))]'}`}>
                        {step.label}
                        {isCurrent && (
                          <span className="ml-2 text-xs font-normal text-primary">(Current)</span>
                        )}
                      </h4>
                      <p className="text-sm text-[rgb(var(--light-secondary))]">{step.description}</p>
                      {isCurrent && history && history.length > 0 && (
                        <p className="mt-1 text-xs text-[rgb(var(--light-secondary))]">
                          Updated {new Date(history[0].createdAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Repair Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardTitle>
            <div className="flex items-center space-x-2">
              <Smartphone className="h-5 w-5 text-primary" />
              <span>Device Details</span>
            </div>
          </CardTitle>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Repair Number</span>
              <span className="font-mono text-sm">{repair.repairNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Device</span>
              <span className="font-medium">{/* Add device name from joined data */}Device</span>
            </div>
            {repair.imei && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">IMEI</span>
                <span className="text-sm">{repair.imei}</span>
              </div>
            )}
            {repair.serialNumber && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Serial Number</span>
                <span className="text-sm">{repair.serialNumber}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Faults</span>
              <span className="text-sm text-right">
                {Array.isArray(repair.faults) && repair.faults.length > 0
                  ? repair.faults.join(', ')
                  : 'None specified'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardTitle>
            <div className="flex items-center space-x-2">
              <Wrench className="h-5 w-5 text-primary" />
              <span>Repair Information</span>
            </div>
          </CardTitle>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Status</span>
              <RepairStatusBadge status={repair.status} size="sm" />
            </div>
            {repair.estimatedCompletion && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Estimated Completion</span>
                <span>{new Date(repair.estimatedCompletion).toLocaleDateString()}</span>
              </div>
            )}
            {repair.estimatedCost && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Estimated Cost</span>
                <span className="font-semibold text-primary">GHS {repair.estimatedCost}</span>
              </div>
            )}
            {repair.completedAt && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Completed</span>
                <span>{new Date(repair.completedAt).toLocaleDateString()}</span>
              </div>
            )}
            {repair.warrantyExpiry && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Warranty Until</span>
                <span>{new Date(repair.warrantyExpiry).toLocaleDateString()}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Description & Notes */}
      {repair.description && (
        <Card>
          <CardTitle>
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-primary" />
              <span>Description</span>
            </div>
          </CardTitle>
          <CardContent>
            <p className="text-[rgb(var(--light-secondary))]">{repair.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {repair.photos && repair.photos.length > 0 && (
        <Card>
          <CardTitle>
            <div className="flex items-center space-x-2">
              <Image className="h-5 w-5 text-primary" />
              <span>Photos</span>
            </div>
          </CardTitle>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {repair.photos.map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={`Device photo ${index + 1}`}
                  className="h-32 w-32 rounded-lg object-cover"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {repair.invoice && (
        <Card>
          <CardTitle>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <span>Repair invoice</span>
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={() => setIsInvoiceCollapsed((value) => !value)}>
                {isInvoiceCollapsed ? <><ChevronDown className="mr-2 h-4 w-4" /> Expand</> : <><ChevronUp className="mr-2 h-4 w-4" /> Collapse</>}
              </Button>
            </div>
          </CardTitle>
          {!isInvoiceCollapsed && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[['Labour', repair.invoice.labour], ['Parts', repair.invoice.parts], ['Service', repair.invoice.service], ['Tax', repair.invoice.tax]].map(([name, value]) => <div key={name} className="rounded-lg bg-[rgb(var(--background))] p-3"><p className="text-xs text-[rgb(var(--light-secondary))]">{name}</p><p className="mt-1 font-semibold">GHS {(Number(value) / 100).toFixed(2)}</p></div>)}
              </div>
              <div className="flex flex-wrap items-end justify-between gap-4 border-t border-[rgb(var(--border))] pt-4"><div><p className="text-sm text-[rgb(var(--light-secondary))]">Total</p><p className="text-2xl font-bold text-primary">GHS {(repair.invoice.total / 100).toFixed(2)}</p><p className="text-sm">Paid GHS {(repair.invoice.paidAmount / 100).toFixed(2)} · Balance GHS {(repair.invoice.balance / 100).toFixed(2)}</p></div><span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{repair.invoice.invoiceStatus.replaceAll('_', ' ')}</span></div>
              {repair.invoice.approvalStatus === 'PENDING' && <Button disabled={approveMutation.isPending} onClick={() => approveMutation.mutate()}>{approveMutation.isPending ? 'Approving...' : 'Approve estimate'}</Button>}
              {repair.invoice.approvalStatus === 'APPROVED' && repair.invoice.balance > 0 && !paymentVerified && <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]"><label className="text-sm">Amount (GHS)<input type="number" min="0.01" max={repair.invoice.balance / 100} step="0.01" value={paymentAmount || defaultPaymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} className="mt-1 w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-3 py-2" /></label><label className="text-sm">Payment method<select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value as typeof paymentMethod)} className="mt-1 w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-3 py-2"><option value="mobile_money">Mobile Money</option><option value="paystack_card">Card</option><option value="bank_transfer">Bank Transfer</option><option value="cash">Cash in-store</option></select></label><Button disabled={payMutation.isPending || !hasValidPayment} onClick={() => payMutation.mutate()}>{payMutation.isPending ? 'Starting...' : 'Pay now'}</Button></div>}
              {paymentMessage && <p className="text-sm text-primary">{paymentMessage}</p>}
            </CardContent>
          )}
        </Card>
      )}
      <div className="flex flex-wrap gap-4">
        <Button variant="secondary">
          <MessageCircle className="mr-2 h-4 w-4" />
          Contact Support
        </Button>
        {repair.status === 'COMPLETED' && (
          <Button variant="primary">
            <Truck className="mr-2 h-4 w-4" />
            Track Delivery
          </Button>
        )}
        {repair.invoice && <Button variant="outline" onClick={async () => { const blob = await repairService.downloadInvoice(repairId); const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${repair.repairNumber}-invoice.pdf`; anchor.click(); URL.revokeObjectURL(url); }}><Download className="mr-2 h-4 w-4" />Download Invoice</Button>}
      </div>
    </div>
  );
};